import {
  CheckCircle2,
  Clock,
  FileMinus,
  FilePlus,
  type LucideIcon,
  PencilLine,
  ShieldCheck,
  XCircle,
} from "lucide-react";

import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import type { Translations } from "@/shared/lang/interfaces/translationTypes";
import useTranslations from "@/shared/lang/useTranslations";
import { formatCurrencyAmount } from "@/shared/utils/currencyMinorUnits";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";
import { formatRail } from "@/shared/utils/railDisplay";

import { useWalletDebitEvents } from "../hooks/useWalletDebitEvents";
import type { WalletDebit, WalletDebitEvent, WalletDebitEventAction } from "../types/walletDebits.types";

const ACTIONS: Record<WalletDebitEventAction, { icon: LucideIcon; key: keyof Translations["walletDebits"] }> =
  {
    reserved: { icon: Clock, key: "timeline_reserved" },
    sent: { icon: CheckCircle2, key: "timeline_sent" },
    cancelled: { icon: XCircle, key: "timeline_cancelled" },
    reference_changed: { icon: PencilLine, key: "timeline_reference_changed" },
    attachments_added: { icon: FilePlus, key: "timeline_attachments_added" },
    attachment_removed: { icon: FileMinus, key: "timeline_attachment_removed" },
  };

/** Every step of the debit, oldest first: what happened, the detail that matters, who and when. */
export function WalletDebitTimeline({ debit }: { debit: WalletDebit }) {
  const { t } = useTranslations("walletDebits");
  // A change of the debit (status, reference, files) reads the timeline again.
  const version = [
    debit.status,
    debit.external_reference ?? "",
    debit.attachments.map((file) => file.id).join(","),
  ].join("|");
  const { events, isLoading, error } = useWalletDebitEvents(debit.id, version);

  return (
    <section aria-labelledby={`debit-timeline-${debit.id}`} className="space-y-3">
      <h3 id={`debit-timeline-${debit.id}`} className="text-sm font-semibold text-foreground">
        {t("timeline_title")}
      </h3>
      {isLoading && events.length === 0 ? (
        <div className="space-y-2" aria-busy="true">
          <ColocaSkeleton className="h-4 w-2/3" />
          <ColocaSkeleton className="h-4 w-1/2" />
        </div>
      ) : error ? (
        <p role="alert" className="text-sm text-destructive">
          {t("timeline_failed", { message: error })}
        </p>
      ) : (
        <ol className="space-y-3 border-l border-border pl-4">
          {events.map((event) => (
            <li key={event.id}>
              <TimelineEntry event={event} currency={debit.currency} />
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

function TimelineEntry({ event, currency }: { event: WalletDebitEvent; currency: string }) {
  const { t, activeLanguage } = useTranslations("walletDebits");
  const { icon: Icon, key } = ACTIONS[event.action] ?? ACTIONS.reserved;
  const detail = describe(event);

  return (
    <div className="relative min-w-0">
      <Icon
        className="absolute top-0.5 -left-[1.4rem] size-3.5 rounded-full bg-background text-muted-foreground"
        aria-hidden="true"
      />
      <p className="text-sm text-foreground">
        <span className="font-medium">{t(key)}</span>
        {detail && <span className="text-muted-foreground"> · {detail}</span>}
      </p>
      <p className="flex flex-wrap items-center gap-x-1.5 text-xs text-muted-foreground">
        <span>{formatDateTime(event.occurred_at, { locale: activeLanguage })}</span>
        <span>· {event.actor_email}</span>
        {event.two_factor_verified && (
          <span className="inline-flex items-center gap-0.5">
            · <ShieldCheck className="size-3" aria-hidden="true" />
            {t("timeline_two_factor")}
          </span>
        )}
      </p>
    </div>
  );

  function describe(entry: WalletDebitEvent): string | null {
    const { metadata } = entry;
    switch (entry.action) {
      case "reserved":
      case "sent": {
        const parts = [
          metadata.net_amount
            ? `${formatCurrencyAmount(metadata.net_amount, currency, activeLanguage)} ${currency}`
            : null,
          metadata.rail ? formatRail(metadata.rail) : null,
          metadata.external_reference ?? null,
        ].filter(Boolean);
        return parts.length ? parts.join(" · ") : null;
      }
      case "reference_changed":
        return t("timeline_reference_detail", {
          from: metadata.from_reference ?? "—",
          to: metadata.to_reference ?? "—",
        });
      case "attachments_added":
      case "attachment_removed":
        return metadata.filenames?.join(", ") ?? null;
      case "cancelled":
        return metadata.reason ?? null;
      default:
        return null;
    }
  }
}
